// ---- Parametric structure docs (building kit v2) ----
// A structure doc may declare named numeric `params` (with defaults), and any
// numeric field in its ops may be authored as a small arithmetic EXPRESSION
// over them ("hw - wallT", "-hw/2 + 1"). Placements (world/structures.json)
// may override params per instance, so one doc describes a family of
// buildings. `resolveStructureDoc` turns an authored doc into the concrete
// numbers-only doc the interpreter (doc.ts) consumes, then RE-PARSES it
// against StructureDocSchema so literal constraints (positive sizes, int
// step counts) still apply to evaluated values.
//
// Pure TS (no THREE, no DOM) — runs identically on the client, the headless
// server, the studio, and the validator. No eval(): a ~40-line recursive-
// descent parser over + - * / ( ), numeric literals, and param identifiers.

import { StructureDocSchema, type StructureDoc } from "../content/schemas.ts";

// Keys inside ops whose STRING values are vocabulary, not expressions — the
// walk below evaluates every other string it meets. New numeric fields get
// expression support automatically; add a key here only if it's a new
// string-valued field.
const STRING_FIELDS = new Set([
  "op", "kind", "side", "axis", "lowAt", "piece", "ridgeAxis", "wallTreatment",
  "finish", "stoop", // mass/roof/base finish enums + stoop side
  "color", "wallColor", "floorColor", "roofColor", "frame", "glass",
  "logColor", "colorDk", "ridgeColor", "capColor",
  "ceilingColor", "corniceColor", "frameColor", "timberColor",
  "style", // window frame style slim|heavy
  // shopSign plaque copy (furnish args) — not arithmetic expressions
  "line1", "line2", "label",
  // `when` is a BOOLEAN expression, not arithmetic — never resolve it as a
  // number here; resolveStructureDoc evaluates + strips it in a dedicated pass.
  "when",
]);

/** Evaluate an arithmetic expression over `vars`. Throws with a message that
 *  names the offending token / identifier (validate.ts surfaces it verbatim). */
export function evalExpr(src: string, vars: Record<string, number>): number {
  let i = 0;
  const peek = () => src[i];
  const skip = () => { while (src[i] === " ") i++; };

  function expr(): number {
    let v = term();
    for (skip(); peek() === "+" || peek() === "-"; skip()) {
      const op = src[i++];
      const r = term();
      v = op === "+" ? v + r : v - r;
    }
    return v;
  }
  function term(): number {
    let v = factor();
    for (skip(); peek() === "*" || peek() === "/"; skip()) {
      const op = src[i++];
      const r = factor();
      v = op === "*" ? v * r : v / r;
    }
    return v;
  }
  function factor(): number {
    skip();
    if (peek() === "-") { i++; return -factor(); }
    if (peek() === "(") {
      i++;
      const v = expr();
      skip();
      if (src[i++] !== ")") throw new Error(`expected ")" in "${src}"`);
      return v;
    }
    const numMatch = /^\d+(\.\d+)?/.exec(src.slice(i));
    if (numMatch) { i += numMatch[0].length; return Number(numMatch[0]); }
    const identMatch = /^[a-zA-Z_][a-zA-Z0-9_]*/.exec(src.slice(i));
    if (identMatch) {
      i += identMatch[0].length;
      const v = vars[identMatch[0]];
      if (v === undefined) {
        throw new Error(`unknown param "${identMatch[0]}" in "${src}" (declared: ${Object.keys(vars).join(", ") || "none"})`);
      }
      return v;
    }
    throw new Error(`unexpected "${src[i] ?? "end of expression"}" in "${src}"`);
  }

  const v = expr();
  skip();
  if (i < src.length) throw new Error(`unexpected "${src[i]}" in "${src}"`);
  if (!Number.isFinite(v)) throw new Error(`"${src}" does not evaluate to a finite number`);
  return v;
}

/** Evaluate a BOOLEAN guard expression (op `when`) over `vars`. Grammar, loosest
 *  to tightest: `||`, `&&`, `!`, comparisons (== != <= >= < >), then the same
 *  arithmetic as `evalExpr`. A bare arithmetic value is truthy iff non-zero, so
 *  `when:"stories"` means "stories != 0". Booleans are carried as 0/1. */
export function evalBool(src: string, vars: Record<string, number>): boolean {
  let i = 0;
  const skip = () => { while (src[i] === " ") i++; };
  const at = (s: string) => { skip(); return src.startsWith(s, i); };

  // orExpr := andExpr ("||" andExpr)*
  function orExpr(): number {
    let v = andExpr();
    for (; at("||"); ) { i += 2; const r = andExpr(); v = v !== 0 || r !== 0 ? 1 : 0; }
    return v;
  }
  // andExpr := cmp ("&&" cmp)*
  function andExpr(): number {
    let v = cmp();
    for (; at("&&"); ) { i += 2; const r = cmp(); v = v !== 0 && r !== 0 ? 1 : 0; }
    return v;
  }
  // cmp := add ((== | != | <= | >= | < | >) add)?
  function cmp(): number {
    const l = add();
    skip();
    for (const op of ["==", "!=", "<=", ">=", "<", ">"]) {
      if (src.startsWith(op, i)) {
        i += op.length;
        const r = add();
        const b =
          op === "==" ? l === r : op === "!=" ? l !== r :
          op === "<=" ? l <= r : op === ">=" ? l >= r :
          op === "<" ? l < r : l > r;
        return b ? 1 : 0;
      }
    }
    return l;
  }
  function add(): number {
    let v = mul();
    for (skip(); src[i] === "+" || src[i] === "-"; skip()) {
      const op = src[i++];
      const r = mul();
      v = op === "+" ? v + r : v - r;
    }
    return v;
  }
  function mul(): number {
    let v = unary();
    for (skip(); src[i] === "*" || src[i] === "/"; skip()) {
      const op = src[i++];
      const r = unary();
      v = op === "*" ? v * r : v / r;
    }
    return v;
  }
  function unary(): number {
    skip();
    // "!" negation, but not the "!=" comparator.
    if (src[i] === "!" && src[i + 1] !== "=") { i++; return unary() === 0 ? 1 : 0; }
    if (src[i] === "-") { i++; return -unary(); }
    if (src[i] === "(") {
      i++;
      const v = orExpr();
      skip();
      if (src[i++] !== ")") throw new Error(`expected ")" in "${src}"`);
      return v;
    }
    const numMatch = /^\d+(\.\d+)?/.exec(src.slice(i));
    if (numMatch) { i += numMatch[0].length; return Number(numMatch[0]); }
    const identMatch = /^[a-zA-Z_][a-zA-Z0-9_]*/.exec(src.slice(i));
    if (identMatch) {
      i += identMatch[0].length;
      const v = vars[identMatch[0]];
      if (v === undefined) {
        throw new Error(`unknown param "${identMatch[0]}" in "${src}" (declared: ${Object.keys(vars).join(", ") || "none"})`);
      }
      return v;
    }
    throw new Error(`unexpected "${src[i] ?? "end of expression"}" in "${src}"`);
  }

  const v = orExpr();
  skip();
  if (i < src.length) throw new Error(`unexpected "${src[i]}" in "${src}"`);
  return v !== 0;
}

/** Deep-copy `node`, evaluating every expression string in a numeric position.
 *  `path` accumulates for error messages ("ops[3].minX: unknown param …"). */
function resolveNode(node: unknown, vars: Record<string, number>, path: string, skipStrings: boolean): unknown {
  if (typeof node === "string" && !skipStrings) {
    try {
      return evalExpr(node, vars);
    } catch (e) {
      throw new Error(`${path}: ${(e as Error).message}`);
    }
  }
  if (Array.isArray(node)) {
    return node.map((v, i) => resolveNode(v, vars, `${path}[${i}]`, skipStrings));
  }
  if (node !== null && typeof node === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node)) {
      // `parts` subtrees are the raw Part vocabulary (colors, prim names,
      // nested modifiers) — left literal in v1, so string-valued Part fields
      // survive untouched.
      out[k] = resolveNode(v, vars, `${path}.${k}`, skipStrings || k === "parts" || STRING_FIELDS.has(k));
    }
    return out;
  }
  return node;
}

/** Whether any string sits in a numeric position (i.e. the doc actually uses
 *  expressions) — lets literal docs skip the resolve/re-parse round-trip. */
function hasExpressions(node: unknown, skipStrings: boolean): boolean {
  if (typeof node === "string") return !skipStrings;
  if (Array.isArray(node)) return node.some((v) => hasExpressions(v, skipStrings));
  if (node !== null && typeof node === "object") {
    return Object.entries(node).some(([k, v]) =>
      hasExpressions(v, skipStrings || k === "parts" || STRING_FIELDS.has(k)));
  }
  return false;
}

/** Resolve an authored doc (+ optional per-placement/variation param overrides)
 *  into the concrete doc the interpreter consumes: drop `when`-guarded ops that
 *  evaluate false, then evaluate every arithmetic expression. `overrides` is the
 *  already-merged param set (a variation's params + any placement params) — see
 *  src/structure/index.ts. Literal docs with no guards pass through untouched. */
export function resolveStructureDoc(doc: StructureDoc, overrides?: Record<string, number>): StructureDoc {
  const declared = doc.params ?? {};
  for (const k of Object.keys(overrides ?? {})) {
    if (!(k in declared)) {
      throw new Error(`param override "${k}" is not declared by structure "${doc.id}" (declared: ${Object.keys(declared).join(", ") || "none"})`);
    }
  }
  const vars = { ...declared, ...overrides };

  // Nothing to do when the doc has neither `when` guards nor expressions. (`when`
  // is a STRING_FIELD, so stripping it never changes hasExpressions — compute it
  // once against doc.ops and reuse it below.)
  // Opening-level `when` (mass/room openings[]) is evaluated too — not only top-level ops.
  const openingWhen = (o: (typeof doc.ops)[number]): boolean => {
    if ((o.op !== "mass" && o.op !== "room") || !("openings" in o) || !o.openings) return false;
    return o.openings.some((opn) => "when" in opn && (opn as { when?: string }).when !== undefined);
  };
  const hasWhen = doc.ops.some((o) => ("when" in o && o.when !== undefined) || openingWhen(o));
  const hasExpr = hasExpressions(doc.ops, false);
  if (!hasWhen && !hasExpr) return doc;

  // Drop ops / openings whose `when` is false under the resolved params, then strip
  // the guard (the interpreter never sees it). A dangling/mis-typed `when` throws
  // here — validate.ts surfaces it, so it can't reach a runtime build.
  const evalWhen = (w: string, where: string): boolean => {
    try { return evalBool(w, vars); }
    catch (e) { throw new Error(`structure "${doc.id}" ${where} when="${w}": ${(e as Error).message}`); }
  };
  const filterOpenings = <T extends { when?: string }>(openings: T[] | undefined, opIx: number): T[] | undefined => {
    if (!openings) return openings;
    return openings.flatMap((opn, i) => {
      const w = opn.when;
      if (w === undefined) return [opn];
      if (!evalWhen(w, `ops[${opIx}].openings[${i}]`)) return [];
      const { when: _drop, ...rest } = opn;
      return [rest as T];
    });
  };
  const ops = !hasWhen ? doc.ops : doc.ops.flatMap((o, opIx) => {
    const w = "when" in o ? o.when : undefined;
    if (w !== undefined && !evalWhen(w, `op "${o.op}"`)) return [];
    let next = o;
    if (w !== undefined) {
      const { when: _drop, ...rest } = o as { when?: string };
      next = rest as typeof o;
    }
    if ((next.op === "mass" || next.op === "room") && "openings" in next) {
      const openings = filterOpenings(next.openings as { when?: string }[] | undefined, opIx);
      next = { ...next, openings } as typeof o;
    }
    return [next];
  });

  const resolvedOps = hasExpr ? resolveNode(ops, vars, "ops", false) : ops;
  const resolved = { ...doc, ops: resolvedOps };
  // Re-parse so schema constraints (positive sizes, int steps) judge the
  // EVALUATED numbers, not just authored literals.
  const res = StructureDocSchema.safeParse(resolved);
  if (!res.success) {
    const issue = res.error.issues[0]!;
    throw new Error(`structure "${doc.id}" resolved to an invalid doc — ${issue.path.join(".")}: ${issue.message}`);
  }
  return res.data;
}
